// Shared token configuration
// Single source of truth for all token addresses and decimals

export type SupportedNetwork = 'ethereum' | 'arbitrum' | 'base' | 'polygon' | 'bsc' | 'avalanche';
export type SupportedToken = 'USDT' | 'USDC' | 'DAI' | 'WETH' | 'WBTC';

export interface TokenConfig {
  address: string;
  decimals: number;
  symbol: string;
  name: string;
}

export interface NetworkConfig {
  chainId: number;
  name: string;
  nativeCurrency: {
    symbol: string;
    name: string;
    decimals: number;
  };
  rpcUrls: string[];
  blockExplorerUrls: string[];
}

// Network configurations
export const NETWORK_CONFIGS: Record<SupportedNetwork, NetworkConfig> = {
  ethereum: {
    chainId: 1,
    name: 'Ethereum',
    nativeCurrency: { symbol: 'ETH', name: 'Ether', decimals: 18 },
    rpcUrls: ['https://rpc.ankr.com/eth'],
    blockExplorerUrls: ['https://etherscan.io']
  },
  arbitrum: {
    chainId: 42161,
    name: 'Arbitrum One',
    nativeCurrency: { symbol: 'ETH', name: 'Ether', decimals: 18 },
    rpcUrls: ['https://rpc.ankr.com/arbitrum'],
    blockExplorerUrls: ['https://arbiscan.io']
  },
  base: {
    chainId: 8453,
    name: 'Base',
    nativeCurrency: { symbol: 'ETH', name: 'Ether', decimals: 18 },
    rpcUrls: ['https://rpc.ankr.com/base'],
    blockExplorerUrls: ['https://basescan.org']
  },
  polygon: {
    chainId: 137,
    name: 'Polygon',
    nativeCurrency: { symbol: 'MATIC', name: 'MATIC', decimals: 18 },
    rpcUrls: ['https://rpc.ankr.com/polygon'],
    blockExplorerUrls: ['https://polygonscan.com']
  },
  bsc: {
    chainId: 56,
    name: 'BNB Chain',
    nativeCurrency: { symbol: 'BNB', name: 'BNB', decimals: 18 },
    rpcUrls: ['https://rpc.ankr.com/bsc'],
    blockExplorerUrls: ['https://bscscan.com']
  },
  avalanche: {
    chainId: 43114,
    name: 'Avalanche C-Chain',
    nativeCurrency: { symbol: 'AVAX', name: 'AVAX', decimals: 18 },
    rpcUrls: ['https://rpc.ankr.com/avalanche'],
    blockExplorerUrls: ['https://snowtrace.io']
  }
};

// Token configurations per network
export const TOKEN_CONFIGS: Record<SupportedNetwork, Record<SupportedToken, TokenConfig>> = {
  ethereum: {
    USDT: {
      address: '0xdAC17F958D2ee523a2206206994597C13D831ec7',
      decimals: 6,
      symbol: 'USDT',
      name: 'Tether USD'
    },
    USDC: {
      address: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48',
      decimals: 6,
      symbol: 'USDC',
      name: 'USD Coin'
    },
    DAI: {
      address: '0x6B175474E89094C44Da98b954EedeAC495271d0F',
      decimals: 18,
      symbol: 'DAI',
      name: 'Dai Stablecoin'
    },
    WETH: {
      address: '0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2',
      decimals: 18,
      symbol: 'WETH',
      name: 'Wrapped Ether'
    },
    WBTC: {
      address: '0x2260FAC5E5542a773Aa44fBCfeDf7C193bc2C599',
      decimals: 8,
      symbol: 'WBTC',
      name: 'Wrapped BTC'
    }
  },
  arbitrum: {
    USDT: {
      address: '0xFd086bC7CD5C481DCC9C85ebE478A1C0b69FCbb9',
      decimals: 6,
      symbol: 'USDT',
      name: 'Tether USD'
    },
    USDC: {
      address: '0xaf88d065e77c8cC2239327C5EDb3A432268e5831',
      decimals: 6,
      symbol: 'USDC',
      name: 'USD Coin'
    },
    DAI: {
      address: '0xDA10009cBd5D07dd0CeCc66161FC93D7c9000da1',
      decimals: 18,
      symbol: 'DAI',
      name: 'Dai Stablecoin'
    },
    WETH: {
      address: '0x82aF49447D8a07e3bd95BD0d56f35241523fBab1',
      decimals: 18,
      symbol: 'WETH',
      name: 'Wrapped Ether'
    },
    WBTC: {
      address: '0x2f2a2543B76A4166549F7aaB2e75Bef0aefC5B0f',
      decimals: 8,
      symbol: 'WBTC',
      name: 'Wrapped BTC'
    }
  },
  base: {
    USDC: {
      address: '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913',
      decimals: 6,
      symbol: 'USDC',
      name: 'USD Coin'
    },
    USDT: {
      address: '0xfde4C96c8593536E31F229EA8f37b2ADa2699bb2',
      decimals: 6,
      symbol: 'USDT',
      name: 'Tether USD'
    },
    DAI: {
      address: '0x50c5725949A6F0c72E6C4a641F24049A917DB0Cb',
      decimals: 18,
      symbol: 'DAI',
      name: 'Dai Stablecoin'
    },
    WETH: {
      address: '0x4200000000000000000000000000000000000006',
      decimals: 18,
      symbol: 'WETH',
      name: 'Wrapped Ether'
    },
    WBTC: {
      address: '0x036CbD53842c5426634e7929541eC2318f3dCF7e',
      decimals: 8,
      symbol: 'WBTC',
      name: 'Wrapped BTC'
    }
  },
  polygon: {
    USDT: {
      address: '0xc2132D05D31c914a87C6611C10748AEb04B58e8F',
      decimals: 6,
      symbol: 'USDT',
      name: 'Tether USD'
    },
    USDC: {
      address: '0x2791Bca1f2de4661ED88A30C99A7a9449Aa84174',
      decimals: 6,
      symbol: 'USDC',
      name: 'USD Coin'
    },
    DAI: {
      address: '0x8f3Cf7ad23Cd3CaDbD9735AFf958023239c6A063',
      decimals: 18,
      symbol: 'DAI',
      name: 'Dai Stablecoin'
    },
    WETH: {
      address: '0x7ceB23fD6bC0adD59E62ac25578270cFf1b9f619',
      decimals: 18,
      symbol: 'WETH',
      name: 'Wrapped Ether'
    },
    WBTC: {
      address: '0x1BFD67037B42Cf73acF2047067bd4F2C47D9BfD6',
      decimals: 8,
      symbol: 'WBTC',
      name: 'Wrapped BTC'
    }
  },
  bsc: {
    USDT: {
      address: '0x55d398326f99059fF775485246999027B3197955',
      decimals: 18,
      symbol: 'USDT',
      name: 'Tether USD'
    },
    USDC: {
      address: '0x8AC76a51cc950d9822D68b83fE1Ad97B32Cd580d',
      decimals: 18,
      symbol: 'USDC',
      name: 'USD Coin'
    },
    DAI: {
      address: '0x1AF3F329e8BE154074D8769D1FFa4eE058B1DBc3',
      decimals: 18,
      symbol: 'DAI',
      name: 'Dai Stablecoin'
    },
    WETH: {
      address: '0x2170Ed0880ac9A755fd29B2688956BD959F933F8',
      decimals: 18,
      symbol: 'WETH',
      name: 'Wrapped Ether'
    },
    WBTC: {
      address: '0x7130d2A12B9BCbFAe4f2634d864A1Ee1Ce3Ead9c',
      decimals: 18,
      symbol: 'WBTC',
      name: 'Wrapped BTC'
    }
  },
  avalanche: {
    USDT: {
      address: '0x9702230A8Ea53601f5cD2dc00fDBc13d4dF4A8c7',
      decimals: 6,
      symbol: 'USDT',
      name: 'Tether USD'
    },
    USDC: {
      address: '0xB97EF9Ef8734C71904D8002F8b6Bc66Dd9c48a6E',
      decimals: 6,
      symbol: 'USDC',
      name: 'USD Coin'
    },
    DAI: {
      address: '0xd586E7F844cEa2F87f50152665BCbc2C279D8d70',
      decimals: 18,
      symbol: 'DAI',
      name: 'Dai Stablecoin'
    },
    WETH: {
      address: '0x49D5c2BdFfac6CE2BFdB6640F4F80f226bc10bAB',
      decimals: 18,
      symbol: 'WETH',
      name: 'Wrapped Ether'
    },
    WBTC: {
      address: '0x50b7545627a5162F82A992c33b87aDc75187B218',
      decimals: 8,
      symbol: 'WBTC',
      name: 'Wrapped BTC'
    }
  }
};

// Utility functions
export function isValidNetwork(network: string): network is SupportedNetwork {
  return Object.keys(NETWORK_CONFIGS).includes(network.toLowerCase());
}

export function isValidToken(token: string): token is SupportedToken {
  return ['USDT', 'USDC', 'DAI', 'WETH', 'WBTC'].includes(token.toUpperCase());
}

export function isNativeToken(token: string, network: SupportedNetwork): boolean {
  const networkConfig = NETWORK_CONFIGS[network];
  return token.toUpperCase() === networkConfig.nativeCurrency.symbol;
}

export function getTokenConfig(network: string, token?: string): TokenConfig | null {
  if (!token || !isValidNetwork(network) || !isValidToken(token)) {
    return null;
  }
  
  return TOKEN_CONFIGS[network][token] || null;
}

export function getNetworkConfig(network: string): NetworkConfig | null {
  if (!isValidNetwork(network)) {
    return null;
  }
  
  return NETWORK_CONFIGS[network];
}

export function getSupportedTokensForNetwork(network: string): SupportedToken[] {
  if (!isValidNetwork(network)) {
    return [];
  }
  
  return Object.keys(TOKEN_CONFIGS[network]) as SupportedToken[];
}

export function getAllSupportedNetworks(): SupportedNetwork[] {
  return Object.keys(NETWORK_CONFIGS) as SupportedNetwork[];
}

// For backward compatibility with existing code
export const FALLBACK_RPC_URLS: Record<number, string> = Object.fromEntries(
  Object.entries(NETWORK_CONFIGS).map(([network, config]) => [
    config.chainId,
    config.rpcUrls[0]
  ])
);

export { type SupportedNetwork as Network, type SupportedToken as Token };